import { Fragment, RefObject, useEffect } from 'react';

import {
  Analytics,
  Container,
  OpenGraph,
  TwitterCards,
} from '@components/atoms';
import {
  WhyWe,
  Locations,
  CommonInfo,
  Advantages,
  AboutCompany,
  Partnerships,
  ApplyToJoin,
  ContactUs,
  Feedback,
} from '@components/organisms';

import { useIsClient } from '@hooks/useIsClient';
import { Paths, scrollTo } from '@utils/scrollTo';
import { SERVICES } from '@shared/constants/whyWe';
import { WHO_WE_ARE } from '@shared/constants/content';
import { FEEDBACKS } from '@shared/constants/feedback';
import { ADVANTAGES } from '@shared/constants/advantages';
import { PARTNERSHIPS } from '@shared/constants/partnerships';

type HomePageProps = {
  refs: RefObject<HTMLDivElement | null>[];
};

function HomePage({ refs }: HomePageProps) {
  const client = useIsClient();
  const [aboutRef, locationsRef, reviewsRef, partnersRef, contactUsRef] = refs;

  useEffect(() => {
    if (client) {
      const path = localStorage.getItem('scrollTo') as Paths;
      const isRefsNotInitialized = refs.some((ref) => !ref);

      if (path && !isRefsNotInitialized) {
        scrollTo(refs as RefObject<HTMLDivElement>[], path);
        localStorage.removeItem('scrollTo');
      }
    }
  }, [client, refs]);

  return (
    <Fragment>
      <Analytics pageTitle="GreenLine Logistics – Efficient Freight and Logistics Services in the USA">
        <meta
          name="description"
          content="GreenLine Logistics  provides comprehensive logistics services, including nationwide freight delivery and warehousing. We focus on speed, efficiency, and tailored supply chain solutions."
        />

        <meta
          name="keywords"
          content="freight delivery, logistics USA, warehousing services,  GreenLine Logistics, transportation company, supply chain logistics"
        />

        <OpenGraph
          title="GreenLine Logistics – Efficient Freight and Logistics Services in the USA"
          description="GreenLine Logistics  provides comprehensive logistics services, including nationwide freight delivery and warehousing. We focus on speed, efficiency, and tailored supply chain solutions."
          url="https://greenlinelog.com/"
        />

        <TwitterCards
          title="GreenLine Logistics – Efficient Freight and Logistics Services in the USA"
          description="GreenLine Logistics  provides comprehensive logistics services, including nationwide freight delivery and warehousing. We focus on speed, efficiency, and tailored supply chain solutions."
          site="https://greenlinelog.com/"
        />
      </Analytics>

      <Container>
        <CommonInfo ref={contactUsRef} />
        <AboutCompany content={WHO_WE_ARE} ref={aboutRef} />
      </Container>

      <Locations ref={locationsRef} />

      <Container>
        <Advantages content={ADVANTAGES} ref={reviewsRef} />
      </Container>

      <Container>
        <WhyWe content={SERVICES} />
      </Container>

      <Partnerships content={PARTNERSHIPS} ref={partnersRef} />

      <Feedback content={FEEDBACKS} ref={contactUsRef} />

      <ApplyToJoin />

      <ContactUs ref={contactUsRef} />
    </Fragment>
  );
}

export default HomePage;
